import { Card, DataRow, Notice } from '@/components/ui'
import type { PlanResponse } from '@/lib/types'

/**
 * Les deux blocs côte à côte exigés au §2 : « Ma saisie » et « Ordre
 * réellement envoyé ».
 *
 * Le parti pris d'affichage : le bloc de droite est celui qui compte, c'est
 * lui qui décrit ce qui partira chez le broker. Il est donc mis en avant
 * (fond plus clair, texte plein) tandis que la saisie est présentée en
 * retrait. Le risque monétaire est toujours calculé sur le stop du bloc de
 * droite — jamais sur celui de gauche.
 */
export function PlanPanel({ plan }: { plan: PlanResponse }) {
  const currency = plan.account_currency

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <div className="grid grid-cols-2 divide-x divide-border">
          <OrderBlock
            label={plan.entry.label}
            side={plan.entry.side}
            lots={plan.entry.lots}
            stop={plan.entry.stop_loss_pips}
            target={plan.entry.take_profit_pips}
            ratio={plan.entry.risk_reward}
            symbol={plan.entry.symbol}
            muted
          />
          <OrderBlock
            label={plan.sent.label}
            side={plan.sent.side}
            lots={plan.sent.lots}
            stop={plan.sent.stop_loss_pips}
            target={plan.sent.take_profit_pips}
            ratio={plan.sent.risk_reward}
            symbol={plan.sent.symbol}
          />
        </div>
      </Card>

      <Card title="Chiffres de l'ordre envoyé">
        <dl className="divide-y divide-border">
          <DataRow
            label="Risque réel"
            value={plan.risk_amount ? `−${plan.risk_amount} ${currency}` : '—'}
            hint={
              plan.risk_percent_of_balance
                ? `${plan.risk_percent_of_balance} % du capital`
                : undefined
            }
            tone={plan.risk_amount ? 'down' : 'muted'}
          />
          <DataRow
            label="Gain potentiel"
            value={plan.reward_amount ? `+${plan.reward_amount} ${currency}` : '—'}
            tone={plan.reward_amount ? 'up' : 'muted'}
          />
          {/*
            Le spread, en argent. Placé JUSTE SOUS le gain visé, parce que
            c'est à lui qu'il faut le comparer : deux pips d'écart sur une
            cible de vingt, c'est un dixième du gain payé avant d'avoir
            commencé. Et c'est le seul coût que l'inversion ne renverse pas —
            permuter le stop et la cible ne le fait pas changer de camp.
          */}
          <DataRow
            label="Spread à l'ouverture"
            value={plan.spread_cost ? `−${plan.spread_cost} ${currency}` : '—'}
            hint={
              plan.spread_pips
                ? `${plan.spread_pips} pips · payé quel que soit le sens`
                : 'aucune cotation en direct'
            }
            tone={plan.spread_cost ? 'down' : 'muted'}
          />
          <DataRow
            label="Valeur du pip"
            value={`${plan.pip_value_total} ${currency}`}
            hint={`${plan.pip_value_per_lot} ${currency} par lot`}
          />
          <DataRow label="Marge requise" value={`${plan.margin_required} ${currency}`} />
          <DataRow
            label="Marge libre après"
            value={`${plan.free_margin_after} ${currency}`}
            tone={plan.free_margin_after.startsWith('-') ? 'down' : 'normal'}
          />
        </dl>
      </Card>

      <Notice tone="error" title="L'ordre ne peut pas partir" items={plan.errors} />
      <Notice tone="warn" title="À lire avant d'envoyer" items={plan.warnings} />

      {/*
        Détail technique. Les valeurs relatives sont celles du protocole
        cTrader ; sur Capital.com les niveaux partent en prix absolus. Le
        titre reste neutre pour ne pas laisser croire qu'on parle au mauvais
        courtier.
      */}
      <Card title="Détail technique de l'ordre">
        <dl className="divide-y divide-border">
          <DataRow label="Volume interne" value={plan.volume.api_volume} />
          <DataRow
            label="Distance du stop"
            value={
              plan.sent.stop_loss_pips ? `${plan.sent.stop_loss_pips} pips` : '—'
            }
            tone={plan.sent.stop_loss_pips ? 'normal' : 'muted'}
          />
          <DataRow
            label="Distance de la cible"
            value={
              plan.sent.take_profit_pips ? `${plan.sent.take_profit_pips} pips` : '—'
            }
            tone={plan.sent.take_profit_pips ? 'normal' : 'muted'}
          />
        </dl>
        {plan.conversion_paths.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
              Conversions utilisées
            </p>
            <ul className="num space-y-0.5 text-[11px] text-ink-muted">
              {plan.conversion_paths.map((path) => (
                <li key={path}>{path}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  )
}

function OrderBlock({
  label,
  symbol,
  side,
  lots,
  stop,
  target,
  ratio,
  muted = false,
}: {
  label: string
  symbol: string
  side: string
  lots: string
  stop: string | null
  target: string | null
  ratio: string | null
  muted?: boolean
}) {
  const buy = side === 'BUY'
  return (
    <div className={`p-3 ${muted ? 'bg-panel' : 'bg-elevated'}`}>
      <p className="mb-2 text-[10px] uppercase tracking-wide text-ink-muted">{label}</p>

      <div className="mb-2 flex items-baseline gap-2">
        <span
          className={`num rounded px-1.5 py-0.5 text-[13px] font-bold ${
            buy ? 'bg-up/20 text-up' : 'bg-down/20 text-down'
          } ${muted ? 'opacity-70' : ''}`}
        >
          {side}
        </span>
        <span className={`num text-[13px] ${muted ? 'text-ink-muted' : 'text-ink'}`}>
          {symbol}
        </span>
      </div>

      <dl className={`space-y-1 text-[12px] ${muted ? 'text-ink-muted' : 'text-ink'}`}>
        <Line label="Volume" value={`${lots} lot`} />
        <Line label="Stop Loss" value={stop ? `${stop} pips` : 'aucun'} absent={!stop} />
        <Line label="Take Profit" value={target ? `${target} pips` : 'aucun'} absent={!target} />
        <Line label="Ratio" value={ratio ? `1:${ratio}` : '—'} />
      </dl>
    </div>
  )
}

function Line({ label, value, absent }: { label: string; value: string; absent?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`num ${absent ? 'text-down' : ''}`}>{value}</dd>
    </div>
  )
}
